import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Add from '@mui/icons-material/Add';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { unitStructureQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { getAllDrivelinesSorted } from 'domain/unit/Unit';
import { getLinkConnectServiceContract } from 'library/components/link/linkUtils';
import { OperationType } from 'library/models/enums/OperationType';
import { useAppSelector } from 'library/redux/hooks';
import {
	UseCheckUserAccessProps,
	checkUserAccess,
} from 'main/auth/checkUserAccess';
import { reactPlugin } from 'main/azureInsights/config';
import { AddingManuallyServiceContractDetails } from 'modules/ServiceContracts/Components/AddingManuallyServiceContractDetails';
import { AddingServiceContractDialog } from 'modules/ServiceContracts/Components/AddingServiceContractDialog';
import { ServiceContractDetails } from 'modules/ServiceContracts/Components/ServiceContractDetails';
import { ServiceContractGrid } from 'modules/ServiceContracts/Components/ServiceContractGrid';
import { ServiceContract } from 'modules/ServiceContracts/Components/types';
import { selectConnectedServiceContracts } from 'modules/ServiceContracts/State/Selectors';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';

const serviceContractsArea: UseCheckUserAccessProps = {
	areaName: AreaName.ServiceContracts,
	permission: OperationType.Write,
};

const serviceContractsManageArea: UseCheckUserAccessProps = {
	areaName: AreaName.ServiceContractsManage,
	permission: OperationType.Write,
};

const ServiceContractsSectionPage = () => {
	const { t } = useTranslation(['commonButton', 'serviceContracts']);
	const queryClient = useQueryClient();
	const { id } = useResolvedUnitIds();
	const navigate = useNavigate();
	const [isAdding, setIsAdding] = useState(false);
	const [isAddingManually, setIsAddingManually] = useState(false);
	const [viewServiceContract, setViewServiceContract] = useState<
		ServiceContract | undefined
	>();
	const unitIds = useResolvedUnitIds();
	const connectedContracts = useAppSelector(
		selectConnectedServiceContracts(id)
	);

	const { data } = useSuspenseQuery(unitStructureQuery(unitIds.id));

	const hasEmob = useMemo(
		() =>
			getAllDrivelinesSorted(data)
				.filter(
					({ isServiceProtocolAvailable }) =>
						isServiceProtocolAvailable
				)
				.filter(({ isEmob }) => isEmob).length > 0,
		[data.installations.length, unitIds.id]
	);

	const canAddEditDelete = checkUserAccess(queryClient, serviceContractsArea);
	const canManage = checkUserAccess(queryClient, serviceContractsManageArea); // connect, pause, reactivate, terminate

	const handleAddingServiceContent = useCallback(
		async (serviceContract?: ServiceContract) => {
			setIsAdding(false);

			if (serviceContract?.id) {
				await navigate(
					getLinkConnectServiceContract(
						unitIds.publicId,
						serviceContract.id
					)
				);
				return;
			}

			setIsAddingManually(true);
		},
		[navigate, unitIds.publicId]
	);

	const handleAddButtonClicked = useCallback(async () => {
		if (canManage) {
			setIsAdding(true);
		} else {
			await handleAddingServiceContent();
		}
	}, [canManage, handleAddingServiceContent]);

	const handleBackToList = useCallback(() => {
		setIsAdding(false);
		setIsAddingManually(false);
		setViewServiceContract(undefined);
	}, []);

	const handleOnView = useCallback((contract: ServiceContract) => {
		setIsAdding(false);
		setIsAddingManually(false);
		setViewServiceContract(contract);
	}, []);

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
				<Typography variant="h6">
					{t('serviceContracts:service-contracts')}
				</Typography>
			</Box>
			{isAddingManually && (
				<AddingManuallyServiceContractDetails
					onView={handleOnView}
					onBackToList={handleBackToList}
					isEmob={hasEmob}
				/>
			)}
			{viewServiceContract && (
				<ServiceContractDetails
					serviceContract={viewServiceContract}
					onBackToList={handleBackToList}
					canManage={canManage}
					canAddEditDelete={canAddEditDelete}
				/>
			)}
			{!viewServiceContract && !isAddingManually && (
				<>
					{(canAddEditDelete || canManage) && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
							}}>
							<Button
								variant="outlined"
								onClick={handleAddButtonClicked}
								startIcon={<Add />}
								sx={{ marginTop: 'auto' }}>
								{t('commonButton:add')}
							</Button>
						</Box>
					)}
					<ServiceContractGrid
						unitId={id}
						onView={handleOnView}
						connectedContracts={connectedContracts}
					/>
				</>
			)}
			{isAdding && canManage && (
				<AddingServiceContractDialog
					onClose={() => setIsAdding(false)}
					onConfirm={handleAddingServiceContent}
					canConnect={canManage}
					canAddManually={canAddEditDelete}
				/>
			)}
		</Box>
	);
};

export default withAITracking(
	reactPlugin,
	ServiceContractsSectionPage,
	'UnitOverviewServiceContractsSectionPage',
	'application-insight-container'
);
